import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Trash2, ZoomIn, ZoomOut, RotateCcw, Crop } from 'lucide-react';

const BACKEND_URL = RUNTIME_BACKEND_URL;
const SNAP_THRESHOLD = 0.8;
const ARROW_STEP = 0.5;
const ARROW_FINE = 0.1;
const PAN_SENSITIVITY = 1.5;

export default function PageDesigner({
  pageWidthCm, pageHeightCm, placedPhotos, onPlacedPhotosChange, albumId, showGuides, onActionComplete,
}) {
  const containerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(null);
  const [alignGuides, setAlignGuides] = useState([]);
  const [cropMode, setCropMode] = useState(null); // index of photo in crop mode

  const actionRef = useRef(null);
  const startMouseRef = useRef(null);
  const startRectRef = useRef(null);
  const activeIdxRef = useRef(null);
  const cropModeRef = useRef(null);
  const photosRef = useRef(placedPhotos);
  const onChangeRef = useRef(onPlacedPhotosChange);
  const widthRef = useRef(pageWidthCm);
  const heightRef = useRef(pageHeightCm);

  useEffect(() => { photosRef.current = placedPhotos; }, [placedPhotos]);
  useEffect(() => { onChangeRef.current = onPlacedPhotosChange; }, [onPlacedPhotosChange]);
  useEffect(() => { activeIdxRef.current = activeIdx; }, [activeIdx]);
  useEffect(() => { cropModeRef.current = cropMode; }, [cropMode]);
  useEffect(() => { widthRef.current = pageWidthCm; }, [pageWidthCm]);
  useEffect(() => { heightRef.current = pageHeightCm; }, [pageHeightCm]);

  const exitCropMode = useCallback(() => setCropMode(null), []);

  // --- Snap ---
  const calcSnap = useCallback((photo, idx) => {
    const others = photosRef.current.filter((_, i) => i !== idx);
    const guides = [];
    let { x, y } = photo;
    const vEdges = [0, 50, 100]; const hEdges = [0, 50, 100];
    others.forEach(p => {
      vEdges.push(p.x, p.x + p.w, p.x + p.w / 2);
      hEdges.push(p.y, p.y + p.h, p.y + p.h / 2);
    });
    let snapX = false;
    for (const mv of [x, x + photo.w, x + photo.w / 2]) {
      if (snapX) break;
      for (const ve of vEdges) {
        if (Math.abs(mv - ve) < SNAP_THRESHOLD) { x += ve - mv; guides.push({ axis: 'v', pos: ve }); snapX = true; break; }
      }
    }
    let snapY = false;
    for (const mh of [y, y + photo.h, y + photo.h / 2]) {
      if (snapY) break;
      for (const he of hEdges) {
        if (Math.abs(mh - he) < SNAP_THRESHOLD) { y += he - mh; guides.push({ axis: 'h', pos: he }); snapY = true; break; }
      }
    }
    return { x, y, guides };
  }, []);

  // --- Gap calc ---
  const gaps = useMemo(() => {
    if (activeIdx === null || !placedPhotos[activeIdx] || cropMode !== null) return null;
    const p = placedPhotos[activeIdx];
    const others = placedPhotos.filter((_, i) => i !== activeIdx);
    const findGap = (edge, dir, axis) => {
      let nearest = dir === 'before' ? 0 : 100;
      others.forEach(o => {
        if (axis === 'x') {
          const oE = dir === 'before' ? o.x + o.w : o.x;
          if (dir === 'before' && oE <= edge && oE > nearest) nearest = oE;
          if (dir === 'after' && oE >= edge && oE < nearest) nearest = oE;
        } else {
          const oE = dir === 'before' ? o.y + o.h : o.y;
          if (dir === 'before' && oE <= edge && oE > nearest) nearest = oE;
          if (dir === 'after' && oE >= edge && oE < nearest) nearest = oE;
        }
      });
      const pct = Math.abs(edge - nearest);
      const cm = axis === 'x' ? pageWidthCm : pageHeightCm;
      return { pct, mm: Math.round(pct / 100 * cm * 10), boundary: nearest };
    };
    return {
      left: findGap(p.x, 'before', 'x'), right: findGap(p.x + p.w, 'after', 'x'),
      top: findGap(p.y, 'before', 'y'), bottom: findGap(p.y + p.h, 'after', 'y'),
    };
  }, [activeIdx, placedPhotos, pageWidthCm, pageHeightCm, cropMode]);

  // --- Keyboard ---
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && cropModeRef.current !== null) {
      setCropMode(null); e.preventDefault(); return;
    }
    if (activeIdxRef.current === null) return;

    // In crop mode: arrows pan the image inside
    if (cropModeRef.current !== null) {
      const step = e.shiftKey ? 1 : 3;
      const photo = { ...photosRef.current[cropModeRef.current] };
      let moved = true;
      const cx = photo.cropX ?? 50, cy = photo.cropY ?? 50;
      switch (e.key) {
        case 'ArrowLeft':  photo.cropX = Math.max(0, cx - step); break;
        case 'ArrowRight': photo.cropX = Math.min(100, cx + step); break;
        case 'ArrowUp':    photo.cropY = Math.max(0, cy - step); break;
        case 'ArrowDown':  photo.cropY = Math.min(100, cy + step); break;
        default: moved = false;
      }
      if (!moved) return;
      e.preventDefault();
      const updated = [...photosRef.current];
      updated[cropModeRef.current] = photo;
      onChangeRef.current(updated);
      return;
    }

    // Normal mode: arrows move frame
    const step = e.shiftKey ? ARROW_FINE : ARROW_STEP;
    const photo = { ...photosRef.current[activeIdxRef.current] };
    let moved = true;
    switch (e.key) {
      case 'ArrowLeft':  photo.x = Math.max(0, photo.x - step); break;
      case 'ArrowRight': photo.x = Math.min(100 - photo.w, photo.x + step); break;
      case 'ArrowUp':    photo.y = Math.max(0, photo.y - step); break;
      case 'ArrowDown':  photo.y = Math.min(100 - photo.h, photo.y + step); break;
      case 'Delete': case 'Backspace':
        onChangeRef.current(photosRef.current.filter((_, i) => i !== activeIdxRef.current));
        setActiveIdx(null); e.preventDefault();
        if (onActionComplete) onActionComplete();
        return;
      default: moved = false;
    }
    if (!moved) return;
    e.preventDefault();
    setAlignGuides([]);
    const updated = [...photosRef.current];
    updated[activeIdxRef.current] = photo;
    onChangeRef.current(updated);
  }, []);

  // --- Wheel zoom in crop mode ---
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (cropModeRef.current === null) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.05 : 0.05;
      const photo = { ...photosRef.current[cropModeRef.current] };
      photo.cropZoom = Math.max(1, Math.min(3, (photo.cropZoom ?? 1) + delta));
      const updated = [...photosRef.current];
      updated[cropModeRef.current] = photo;
      onChangeRef.current(updated);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // --- Double-click → toggle crop mode ---
  const handleDoubleClick = (e, idx) => {
    e.preventDefault(); e.stopPropagation();
    if (cropMode === idx) { setCropMode(null); } // exit
    else { setCropMode(idx); setActiveIdx(idx); } // enter
    containerRef.current?.focus();
  };

  // --- DROP ---
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; };
  const handleDrop = (e) => {
    e.preventDefault();
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (!containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      const px = ((e.clientX - r.left) / r.width) * 100;
      const py = ((e.clientY - r.top) / r.height) * 100;
      const ratio = widthRef.current / heightRef.current;
      const w = ratio >= 1.5 ? 28 : 35;
      const h = ratio >= 1.5 ? 90 : 55;
      const updated = [...photosRef.current, {
        id: data.id,
        x: Math.max(0, Math.min(100 - w, px - w / 2)),
        y: Math.max(0, Math.min(100 - h, py - h / 2)),
        w, h, cropX: 50, cropY: 50, cropZoom: 1,
      }];
      onChangeRef.current(updated);
      setActiveIdx(updated.length - 1);
      setCropMode(null);
      containerRef.current?.focus();
      if (onActionComplete) onActionComplete();
    } catch (err) { /* ignore */ }
  };

  // --- MOVE / RESIZE / CROP-PAN ---
  const startAction = (e, idx, type) => {
    e.preventDefault(); e.stopPropagation();
    // In crop mode, drag = pan image inside frame
    const actualType = (cropModeRef.current === idx && type === 'move') ? 'crop-pan' : type;
    setActiveIdx(idx); activeIdxRef.current = idx;
    actionRef.current = actualType;
    if (!containerRef.current) return;
    const r = containerRef.current.getBoundingClientRect();
    startMouseRef.current = { px: ((e.clientX - r.left) / r.width) * 100, py: ((e.clientY - r.top) / r.height) * 100 };
    startRectRef.current = { ...photosRef.current[idx] };
    containerRef.current?.focus();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!actionRef.current || !startMouseRef.current || !containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      const px = ((e.clientX - r.left) / r.width) * 100;
      const py = ((e.clientY - r.top) / r.height) * 100;
      const dx = px - startMouseRef.current.px;
      const dy = py - startMouseRef.current.py;
      const s = { ...startRectRef.current };
      const act = actionRef.current;

      if (act === 'crop-pan') {
        // Pan image inside frame: drag right → show more left → decrease cropX
        s.cropX = Math.max(0, Math.min(100, (s.cropX ?? 50) - dx * PAN_SENSITIVITY));
        s.cropY = Math.max(0, Math.min(100, (s.cropY ?? 50) - dy * PAN_SENSITIVITY));
      } else if (act === 'move') {
        s.x = Math.max(0, Math.min(100 - s.w, s.x + dx));
        s.y = Math.max(0, Math.min(100 - s.h, s.y + dy));
        const snap = calcSnap(s, activeIdxRef.current);
        s.x = snap.x; s.y = snap.y; setAlignGuides(snap.guides);
      } else {
        setAlignGuides([]);
        if (act === 'resize-se') { s.w = Math.max(5, Math.min(100 - s.x, s.w + dx)); s.h = Math.max(5, Math.min(100 - s.y, s.h + dy)); }
        else if (act === 'resize-sw') { const nw = Math.max(5, s.w - dx); s.x = s.x + s.w - nw; s.w = nw; s.h = Math.max(5, Math.min(100 - s.y, s.h + dy)); }
        else if (act === 'resize-ne') { s.w = Math.max(5, Math.min(100 - s.x, s.w + dx)); const nh = Math.max(5, s.h - dy); s.y = s.y + s.h - nh; s.h = nh; }
        else if (act === 'resize-nw') { const nw = Math.max(5, s.w - dx); s.x = s.x + s.w - nw; s.w = nw; const nh = Math.max(5, s.h - dy); s.y = s.y + s.h - nh; s.h = nh; }
        else if (act === 'resize-n') { const nh = Math.max(5, s.h - dy); s.y = s.y + s.h - nh; s.h = nh; }
        else if (act === 'resize-s') { s.h = Math.max(5, Math.min(100 - s.y, s.h + dy)); }
        else if (act === 'resize-e') { s.w = Math.max(5, Math.min(100 - s.x, s.w + dx)); }
        else if (act === 'resize-w') { const nw = Math.max(5, s.w - dx); s.x = s.x + s.w - nw; s.w = nw; }
      }
      const updated = [...photosRef.current];
      updated[activeIdxRef.current] = s;
      onChangeRef.current(updated);
    };
    const onUp = () => {
      const hadAction = actionRef.current !== null;
      actionRef.current = null; startMouseRef.current = null; startRectRef.current = null; setAlignGuides([]);
      if (hadAction && onActionComplete) onActionComplete();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [calcSnap]);

  const removePhoto = (idx) => {
    onChangeRef.current(photosRef.current.filter((_, i) => i !== idx));
    setActiveIdx(null); setCropMode(null);
    if (onActionComplete) onActionComplete();
  };

  // Crop mode zoom controls
  const adjustCropZoom = (idx, delta) => {
    const photo = { ...photosRef.current[idx] };
    photo.cropZoom = Math.max(1, Math.min(3, (photo.cropZoom ?? 1) + delta));
    const updated = [...photosRef.current]; updated[idx] = photo;
    onChangeRef.current(updated);
  };
  const resetCrop = (idx) => {
    const photo = { ...photosRef.current[idx] };
    photo.cropX = 50; photo.cropY = 50; photo.cropZoom = 1;
    const updated = [...photosRef.current]; updated[idx] = photo;
    onChangeRef.current(updated);
  };

  const guideLines = [10, 20, 30, 40, 50, 60, 70, 80, 90];
  const activePhoto = activeIdx !== null ? placedPhotos[activeIdx] : null;
  const isCropMode = cropMode !== null;

  return (
    <div
      ref={containerRef}
      data-testid="page-canvas"
      tabIndex={0}
      className="relative bg-white border border-gray-300 shadow-sm select-none outline-none"
      style={{ aspectRatio: `${pageWidthCm} / ${pageHeightCm}`, width: '100%', maxHeight: '100%' }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        if (e.target === containerRef.current) { setActiveIdx(null); setCropMode(null); }
      }}
    >
      {/* Grid guides */}
      {showGuides && !isCropMode && (
        <div className="absolute inset-0 pointer-events-none z-0">
          {guideLines.map(pct => (
            <div key={`v${pct}`} className="absolute top-0 bottom-0"
              style={{ left: `${pct}%`, borderLeft: pct === 50 ? '1px dashed rgba(220,50,50,0.2)' : '1px dashed rgba(59,130,246,0.1)' }} />
          ))}
          {guideLines.map(pct => (
            <div key={`h${pct}`} className="absolute left-0 right-0"
              style={{ top: `${pct}%`, borderTop: pct === 50 ? '1px dashed rgba(220,50,50,0.2)' : '1px dashed rgba(59,130,246,0.1)' }} />
          ))}
        </div>
      )}

      {/* Snap guides */}
      {alignGuides.map((g, i) => (
        <div key={i} className="absolute pointer-events-none z-30"
          style={g.axis === 'v'
            ? { left: `${g.pos}%`, top: 0, bottom: 0, borderLeft: '1px solid rgba(236,72,153,0.7)' }
            : { top: `${g.pos}%`, left: 0, right: 0, borderTop: '1px solid rgba(236,72,153,0.7)' }} />
      ))}

      {/* Placed photos */}
      {placedPhotos.map((photo, idx) => {
        const inCrop = cropMode === idx;
        const isActive = activeIdx === idx;
        const cz = photo.cropZoom ?? 1;
        const cx = photo.cropX ?? 50;
        const cy = photo.cropY ?? 50;

        return (
          <div
            key={`${photo.id}-${idx}`}
            data-testid={`placed-photo-${idx}`}
            className={`absolute overflow-hidden ${
              inCrop ? 'ring-2 ring-dashed ring-pink-500 z-30 shadow-xl'
              : isActive ? 'ring-2 ring-blue-500 z-20 shadow-lg'
              : 'z-10 hover:ring-1 hover:ring-blue-400/60'
            }`}
            style={{
              left: `${photo.x}%`, top: `${photo.y}%`,
              width: `${photo.w}%`, height: `${photo.h}%`,
              cursor: inCrop ? 'move' : (actionRef.current === 'move' && activeIdxRef.current === idx ? 'grabbing' : 'grab'),
            }}
            onMouseDown={(e) => startAction(e, idx, 'move')}
            onClick={(e) => {
              e.stopPropagation();
              if (cropMode !== null && cropMode !== idx) setCropMode(null);
              setActiveIdx(idx);
              containerRef.current?.focus();
            }}
            onDoubleClick={(e) => handleDoubleClick(e, idx)}
          >
            {/* Photo image with crop params */}
            <img
              src={`${BACKEND_URL}/api/albums/${albumId}/photos/${photo.id}/file`}
              alt="" draggable={false}
              className="pointer-events-none"
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover',
                objectPosition: `${cx}% ${cy}%`,
                transform: `scale(${cz})`,
                transformOrigin: `${cx}% ${cy}%`,
              }}
            />

            {/* Crop mode overlay & controls */}
            {inCrop && (
              <>
                {/* Crop mode indicator border (animated) */}
                <div className="absolute inset-0 pointer-events-none border-2 border-dashed border-pink-400 animate-pulse" />
                {/* Crop toolbar */}
                <div className="absolute -bottom-9 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-gray-900/90 text-white rounded-full px-2 py-1 z-40 shadow-lg"
                  onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                  <button className="hover:bg-white/20 rounded p-0.5" onClick={() => adjustCropZoom(idx, -0.1)}
                    data-testid="crop-zoom-out"><ZoomOut className="h-3.5 w-3.5" /></button>
                  <span className="text-[10px] font-mono min-w-[32px] text-center">{Math.round(cz * 100)}%</span>
                  <button className="hover:bg-white/20 rounded p-0.5" onClick={() => adjustCropZoom(idx, 0.1)}
                    data-testid="crop-zoom-in"><ZoomIn className="h-3.5 w-3.5" /></button>
                  <div className="w-px h-3 bg-white/30 mx-0.5" />
                  <button className="hover:bg-white/20 rounded p-0.5 text-[10px]" onClick={() => resetCrop(idx)}
                    data-testid="crop-reset"><RotateCcw className="h-3 w-3" /></button>
                </div>
                {/* Hint */}
                <div className="absolute top-1 left-1/2 -translate-x-1/2 bg-pink-500 text-white text-[9px] px-2 py-0.5 rounded-full pointer-events-none z-40 whitespace-nowrap">
                  Sürükle: kaydır | Tekerlek: yakınlaştır | Esc: çık
                </div>
              </>
            )}

            {/* Normal mode: selection handles */}
            {isActive && !inCrop && (
              <>
                <button data-testid={`delete-photo-${idx}`}
                  className="absolute -top-3 -right-3 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow z-30"
                  onClick={(e) => { e.stopPropagation(); removePhoto(idx); }}>
                  <Trash2 className="h-3 w-3" />
                </button>
                {/* Crop mode button */}
                <button data-testid={`crop-btn-${idx}`}
                  className="absolute -top-3 left-1/2 -translate-x-1/2 bg-pink-500 hover:bg-pink-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow z-30"
                  onClick={(e) => { e.stopPropagation(); setCropMode(idx); }}>
                  <Crop className="h-3 w-3" />
                </button>
                {/* Corner handles */}
                <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-blue-500 rounded-sm cursor-se-resize z-30 border border-white"
                  onMouseDown={(e) => startAction(e, idx, 'resize-se')} />
                <div className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-blue-500 rounded-sm cursor-sw-resize z-30 border border-white"
                  onMouseDown={(e) => startAction(e, idx, 'resize-sw')} />
                <div className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-blue-500 rounded-sm cursor-ne-resize z-30 border border-white"
                  onMouseDown={(e) => startAction(e, idx, 'resize-ne')} />
                <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-blue-500 rounded-sm cursor-nw-resize z-30 border border-white"
                  onMouseDown={(e) => startAction(e, idx, 'resize-nw')} />
                {/* Edge midpoint handles */}
                <div className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-2.5 h-5 bg-blue-500 rounded-sm cursor-e-resize z-30 border border-white"
                  onMouseDown={(e) => startAction(e, idx, 'resize-e')} />
                <div className="absolute top-1/2 -left-1.5 -translate-y-1/2 w-2.5 h-5 bg-blue-500 rounded-sm cursor-w-resize z-30 border border-white"
                  onMouseDown={(e) => startAction(e, idx, 'resize-w')} />
                <div className="absolute left-1/2 -top-1.5 -translate-x-1/2 w-5 h-2.5 bg-blue-500 rounded-sm cursor-n-resize z-30 border border-white"
                  onMouseDown={(e) => startAction(e, idx, 'resize-n')} />
                <div className="absolute left-1/2 -bottom-1.5 -translate-x-1/2 w-5 h-2.5 bg-blue-500 rounded-sm cursor-s-resize z-30 border border-white"
                  onMouseDown={(e) => startAction(e, idx, 'resize-s')} />
              </>
            )}
          </div>
        );
      })}

      {/* Gap measurements */}
      {activePhoto && gaps && !isCropMode && (
        <div className="pointer-events-none z-40 absolute inset-0">
          {gaps.left.pct > 0.3 && (
            <div className="absolute flex items-center" style={{
              left: `${gaps.left.boundary}%`, width: `${gaps.left.pct}%`,
              top: `${activePhoto.y + activePhoto.h / 2}%`, transform: 'translateY(-50%)',
            }}>
              <div className="w-full border-t border-dashed border-orange-500 relative">
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-orange-500 text-white text-[11px] font-semibold font-mono px-1.5 py-0.5 rounded shadow whitespace-nowrap">{gaps.left.mm}mm</span>
              </div>
            </div>
          )}
          {gaps.right.pct > 0.3 && (
            <div className="absolute flex items-center" style={{
              left: `${activePhoto.x + activePhoto.w}%`, width: `${gaps.right.pct}%`,
              top: `${activePhoto.y + activePhoto.h / 2}%`, transform: 'translateY(-50%)',
            }}>
              <div className="w-full border-t border-dashed border-orange-500 relative">
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-orange-500 text-white text-[11px] font-semibold font-mono px-1.5 py-0.5 rounded shadow whitespace-nowrap">{gaps.right.mm}mm</span>
              </div>
            </div>
          )}
          {gaps.top.pct > 0.3 && (
            <div className="absolute flex justify-center" style={{
              top: `${gaps.top.boundary}%`, height: `${gaps.top.pct}%`,
              left: `${activePhoto.x + activePhoto.w / 2}%`, transform: 'translateX(-50%)',
            }}>
              <div className="h-full border-l border-dashed border-orange-500 relative">
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-orange-500 text-white text-[11px] font-semibold font-mono px-1.5 py-0.5 rounded shadow whitespace-nowrap">{gaps.top.mm}mm</span>
              </div>
            </div>
          )}
          {gaps.bottom.pct > 0.3 && (
            <div className="absolute flex justify-center" style={{
              top: `${activePhoto.y + activePhoto.h}%`, height: `${gaps.bottom.pct}%`,
              left: `${activePhoto.x + activePhoto.w / 2}%`, transform: 'translateX(-50%)',
            }}>
              <div className="h-full border-l border-dashed border-orange-500 relative">
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-orange-500 text-white text-[11px] font-semibold font-mono px-1.5 py-0.5 rounded shadow whitespace-nowrap">{gaps.bottom.mm}mm</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {placedPhotos.length === 0 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-300 pointer-events-none gap-1">
          <svg className="h-12 w-12 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-xs">Fotoğrafları alttan sürükleyip buraya bırakın</p>
          <p className="text-[10px] opacity-50">Çift tıkla: kırpma modu | Ok tuşları: hareket</p>
        </div>
      )}
    </div>
  );
}
